import { action, computed, makeAutoObservable, reaction } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { createContext, useContext, useEffect, useRef } from "react";
import { v4 } from "uuid";

import BlockFooter from "./BlockFooter";
import BlockHeader from "./BlockHeader";
import BlockLayout from "./BlockLayout";
import { BlockEnv, EnvProvider } from "./env-context";
import { ReadonlyProvider } from "./readonly-context";
import { BlockPayload, ValidationSchema, ValidationStore } from "./validation";

import { BlockProps } from ".";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDeepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) {
    return true;
  }

  if (obj1 === null || obj2 === null) {
    return obj1 === obj2;
  }

  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if (typeof obj1 !== "object") {
    return obj1 === obj2;
  }

  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }
    if (!isDeepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class BlockStore<TConfiguration = any> {
  configuration?: TConfiguration;
  name = "";
  allSources?: Record<string, string[]>;
  validationSchema?: ValidationSchema<BlockPayload<TConfiguration>>;
  validationStore?: ValidationStore<TConfiguration>;
  id: BlockProps["id"] = `default_block_id_${v4()}`;
  hasDefaultConfiguration = false;
  private _lastSavedPayload: string | null = null;
  isDefaultName = true;
  private _onChange?: (payload: BlockPayload<TConfiguration>) => void;
  private _changeReactionDisposer?: () => void;
  private _suppressOnChange = false;
  private _suppressNextOnChange = false;

  // eslint-disable-next-line max-params
  constructor(
    initialConfiguration?: TConfiguration,
    initialName?: string,
    initialId?: BlockProps["id"],
    validationSchema?: ValidationSchema<BlockPayload<TConfiguration>>,
    initialAllSources?: Record<string, string[]>,
    hasDefaultConfiguration?: boolean,
    initialIsDefaultName?: boolean,
    onChange?: (payload: BlockPayload<TConfiguration>) => void,
  ) {
    if (initialId) {
      this.id = initialId;
    }
    this.configuration = initialConfiguration;
    this.name = initialName || "";
    this.allSources = initialAllSources;
    this.validationSchema = validationSchema;
    this.hasDefaultConfiguration = hasDefaultConfiguration || false;
    this.isDefaultName = initialIsDefaultName !== undefined ? initialIsDefaultName : true;
    this._onChange = onChange;

    if (validationSchema) {
      this.validationStore = new ValidationStore(validationSchema, () => this.payload);
    }

    this._lastSavedPayload = JSON.stringify(this.payload);

    makeAutoObservable(this, {
      setConfiguration: action,
      setValidationSchema: action,
      payload: computed,
      hasChanges: computed,
      lastSavedPayload: computed,
      markAsSaved: action,
      setNameFromUser: action,
      setIsDefaultName: action,
      setOnChange: action,
      dispose: false,
    });

    this.setupOnChangeReaction();
  }

  setName = action((name: string) => {
    this.name = name;
  });

  setNameFromUser = action((name: string) => {
    this.name = name;
    this.isDefaultName = false;
  });

  setIsDefaultName = action((isDefault: boolean) => {
    this.isDefaultName = isDefault;
  });

  setOnChange = action((onChange?: (payload: BlockPayload<TConfiguration>) => void) => {
    this._onChange = onChange;
    this.setupOnChangeReaction();
  });

  private setupOnChangeReaction = () => {
    if (this._changeReactionDisposer) {
      this._changeReactionDisposer();
    }

    if (this._onChange) {
      this._changeReactionDisposer = reaction(
        () => this.payload,
        payload => {
          if (this._suppressOnChange || this._suppressNextOnChange) {
            this._suppressNextOnChange = false;
            return;
          }
          this._onChange?.(payload);
        },
        {
          fireImmediately: this.hasDefaultConfiguration,
          delay: 0,
        },
      );
    }
  };

  get payload(): BlockPayload<TConfiguration> {
    return {
      name: this.name,
      configuration: this.configuration as TConfiguration,
      allSources: this.allSources,
    };
  }

  get hasChanges(): boolean {
    if (this.hasDefaultConfiguration) {
      return true;
    }

    if (!this._lastSavedPayload) {
      return false;
    }
    return JSON.stringify(this.payload) !== this._lastSavedPayload;
  }

  get lastSavedPayload(): string | null {
    return this._lastSavedPayload;
  }

  markAsSaved = action(() => {
    this._lastSavedPayload = JSON.stringify(this.payload);
    this.hasDefaultConfiguration = false;
  });

  setConfiguration = action((configuration: TConfiguration) => {
    this.configuration = configuration;
    // Trigger onChange immediately for synchronous updates
    if (this._onChange) {
      this._onChange(this.payload);
    }
  });

  replaceConfigurationFromProps = action((configuration: TConfiguration) => {
    this._suppressOnChange = true;
    this._suppressNextOnChange = true;
    try {
      this.configuration = configuration;
    } finally {
      this._suppressOnChange = false;
    }
  });

  setAllSources = action((allSources: Record<string, string[]>) => {
    this.allSources = allSources;
    // Trigger onChange immediately for synchronous updates
    if (this._onChange) {
      this._onChange(this.payload);
    }
  });

  replaceAllSourcesFromProps = action((allSources: Record<string, string[]>) => {
    this._suppressOnChange = true;
    this._suppressNextOnChange = true;
    try {
      this.allSources = allSources;
    } finally {
      this._suppressOnChange = false;
    }
  });

  setValidationSchema = action((schema: ValidationSchema<BlockPayload<TConfiguration>>) => {
    this.validationSchema = schema;
    // Re-create validation store with new schema
    if (this.validationStore) {
      this.validationStore.dispose();
    }
    this.validationStore = new ValidationStore(schema, () => this.payload);
  });

  dispose = (): void => {
    if (this.validationStore) {
      this.validationStore.dispose();
    }
    if (this._changeReactionDisposer) {
      this._changeReactionDisposer();
    }
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BlockStoreContext = createContext<BlockStore<any> | null>(null);

export const BlockStoreProvider = <TConfiguration,>({
  children,
  initialConfiguration,
  initialName,
  validationSchema,
  allSources,
  initialId,
  readonly = false,
  env,
  hasDefaultConfiguration = false,
  initialIsDefaultName,
  onChange,
}: {
  children: React.ReactNode;
  initialConfiguration?: TConfiguration;
  initialName?: string;
  initialId?: BlockProps["id"];
  validationSchema?: ValidationSchema<BlockPayload<TConfiguration>>;
  allSources?: Record<string, string[]>;
  readonly?: boolean;
  env?: BlockEnv;
  hasDefaultConfiguration?: boolean;
  initialIsDefaultName?: boolean;
  onChange?: (payload: BlockPayload<TConfiguration>) => void;
}) => {
  // Create store only once with initial values
  const store = useLocalObservable(
    () =>
      new BlockStore(
        initialConfiguration,
        initialName,
        initialId,
        validationSchema,
        allSources,
        hasDefaultConfiguration,
        initialIsDefaultName,
        onChange,
      ),
  );

  // Track initialization state to prevent feedback loops
  const initializationRef = useRef({
    isInitialized: false,
    initialConfiguration: initialConfiguration,
    initialName: initialName,
    initialIsDefaultName: initialIsDefaultName,
  });

  useEffect(() => {
    if (!initializationRef.current.isInitialized) {
      initializationRef.current.isInitialized = true;
      initializationRef.current.initialConfiguration = initialConfiguration;
      initializationRef.current.initialName = initialName;
      initializationRef.current.initialIsDefaultName = initialIsDefaultName;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initializationRef.current.isInitialized) {
      return;
    }

    if (
      initialConfiguration !== undefined &&
      !isDeepEqual(initialConfiguration, store.configuration)
    ) {
      store.replaceConfigurationFromProps(initialConfiguration);
      store.markAsSaved();
    }
  }, [initialConfiguration, store]);

  useEffect(() => {
    if (!initializationRef.current.isInitialized) {
      return;
    }

    if (initialName !== undefined && initialName !== store.name) {
      store.setName(initialName);
      store.markAsSaved();
    }
  }, [initialName, store]);

  useEffect(() => {
    if (!initializationRef.current.isInitialized) {
      return;
    }

    if (validationSchema && validationSchema !== store.validationSchema) {
      store.setValidationSchema(validationSchema);
    }
  }, [validationSchema, store]);

  useEffect(() => {
    if (!initializationRef.current.isInitialized) {
      return;
    }

    if (allSources && !isDeepEqual(allSources, store.allSources)) {
      store.replaceAllSourcesFromProps(allSources);
      store.markAsSaved();
    }
  }, [allSources, store]);

  useEffect(() => {
    store.setOnChange(onChange);
  }, [onChange, store]);

  useEffect(
    () => () => {
      store.dispose();
    },
    [store],
  );

  return (
    <BlockStoreContext.Provider value={store}>
      <ReadonlyProvider readonly={readonly}>
        <EnvProvider env={env}>{children}</EnvProvider>
      </ReadonlyProvider>
    </BlockStoreContext.Provider>
  );
};

export const useBlock = <TConfiguration,>(): BlockStore<TConfiguration> => {
  const blockStore = useContext(BlockStoreContext);
  if (!blockStore) {
    throw new Error("useBlock must be used within a BlockStoreProvider");
  }
  return blockStore as BlockStore<TConfiguration>;
};

// Hook to get validation state for the current block
export const useBlockValidation = <TConfiguration,>() => {
  const blockStore = useBlock<TConfiguration>();

  if (!blockStore.validationStore) {
    // Return a dummy validation object if no validation store exists
    return {
      validate: () => ({ isValid: true, errors: {}, warnings: {} }),
      triggerSaveValidation: () => ({
        isValid: true,
        errors: {},
        warnings: {},
      }),
      markFieldAsTouched: () => undefined,
      result: { isValid: true, errors: {}, warnings: {} },
      isValid: true,
      errors: {},
      warnings: {},
      getFieldError: () => undefined,
      getFieldWarning: () => undefined,
      isFieldValid: () => true,
      hasFieldWarning: () => false,
      hasValidated: false,
      touchedFields: new Set<string>(),
      hasTriggeredSave: false,
    };
  }

  return {
    validate: blockStore.validationStore.validate,
    triggerSaveValidation: blockStore.validationStore.triggerSaveValidation,
    markFieldAsTouched: blockStore.validationStore.markFieldAsTouched,
    result: blockStore.validationStore.result,
    isValid: blockStore.validationStore.result.isValid,
    errors: blockStore.validationStore.result.errors,
    warnings: blockStore.validationStore.result.warnings,
    getFieldError: blockStore.validationStore.getFieldError,
    getFieldWarning: blockStore.validationStore.getFieldWarning,
    isFieldValid: blockStore.validationStore.isFieldValid,
    hasFieldWarning: blockStore.validationStore.hasFieldWarning,
    hasValidated: blockStore.validationStore.hasValidated,
    touchedFields: blockStore.validationStore.touchedFields,
    hasTriggeredSave: blockStore.validationStore.hasTriggeredSave,
  };
};

interface WithBlockStoreOptions<TConfiguration> {
  validationSchema?: ValidationSchema<BlockPayload<TConfiguration>>;
  defaultConfiguration?: TConfiguration;
}

const BlockContent = observer(
  <TConfiguration,>({
    Component,
    DefaultFields,
    type,
    onSave,
    onClose,
    sources,
    sourcesV2,
    configuration,
    name,
    readonly,
    onTypeChange,
    isDraft,
    id,
    env,
    headerInjection,
    banner,
    showSaveButton = true,
    forceOpenVariableSelector,
  }: {
    Component: React.ComponentType<Omit<BlockProps<TConfiguration>, "DefaultFields">>;
    DefaultFields: React.ComponentType<Omit<BlockProps<TConfiguration>, "DefaultFields">>;
  } & Omit<BlockProps<TConfiguration>, "DefaultFields">) => {
    const shouldPassSaveHandlersToDefaultFields = DefaultFields.displayName !== "DefaultFields";

    return (
      <BlockLayout
        banner={banner}
        header={isScrolled => (
          <BlockHeader
            type={type}
            isDraft={isDraft}
            onTypeChange={onTypeChange}
            isScrolled={isScrolled}
            headerInjection={headerInjection}
          />
        )}
        footer={showSaveButton ? <BlockFooter onSave={onSave} /> : null}
      >
        <DefaultFields
          type={type}
          configuration={configuration}
          name={name}
          onSave={shouldPassSaveHandlersToDefaultFields ? onSave : undefined}
          readonly={readonly}
          onClose={onClose}
          sources={sources}
          sourcesV2={sourcesV2}
          id={id}
          env={env}
        />
        <Component
          type={type}
          configuration={configuration}
          name={name}
          onSave={onSave}
          onClose={onClose}
          readonly={readonly}
          sources={sources}
          sourcesV2={sourcesV2}
          id={id}
          env={env}
          forceOpenVariableSelector={forceOpenVariableSelector}
        />
      </BlockLayout>
    );
  },
);

export const withBlockStore = <TConfiguration,>(
  Component: React.ComponentType<Omit<BlockProps<TConfiguration>, "DefaultFields">>,
  options: WithBlockStoreOptions<TConfiguration> = {},
) => {
  const BlockWrapper = React.memo<BlockProps<TConfiguration>>(props => {
    const {
      DefaultFields,
      readonly = false,
      env,
      onChange,
      headerInjection,
      banner,
      ...restProps
    } = props;
    const { validationSchema, defaultConfiguration } = options;

    const isEmpty = Object.values(restProps.configuration ?? {}).length === 0;

    return (
      <BlockStoreProvider<TConfiguration>
        initialConfiguration={!isEmpty ? restProps.configuration : defaultConfiguration}
        initialName={restProps.name}
        initialId={restProps.id}
        validationSchema={validationSchema}
        readonly={readonly}
        env={env}
        allSources={restProps.allSources ?? restProps.sources}
        hasDefaultConfiguration={isEmpty && !!defaultConfiguration}
        onChange={onChange}
      >
        <BlockContent
          Component={Component}
          DefaultFields={DefaultFields}
          {...restProps}
          configuration={!isEmpty ? restProps.configuration : defaultConfiguration}
          readonly={readonly}
          env={env}
          headerInjection={headerInjection}
          banner={banner}
        />
      </BlockStoreProvider>
    );
  });

  BlockWrapper.displayName = `withBlockStore(${
    Component.displayName || Component.name || "Component"
  })`;

  return BlockWrapper;
};

export default BlockStore;
